/**
 * Projects Page - Clean Design
 */

import React, { useState, useEffect } from 'react';
import { useAppStore } from '../stores';
import { useNavigate } from 'react-router-dom';

const API_URL = import.meta.env.VITE_API_URL || '';

type ProjectTemplate = 'pages' | 'blog' | 'quotes' | 'music' | 'wallpapers' | 'custom';

interface NewProject {
    name: string;
    appId: string;
    appUrl: string;
    description: string;
    template: ProjectTemplate;
}

const TEMPLATES = [
    { id: 'pages' as ProjectTemplate, icon: '⚖️', name: 'Legal Pages', desc: 'Privacy, Terms, About Documents' },
    { id: 'blog' as ProjectTemplate, icon: '📜', name: 'Policy Docs', desc: 'Internal Policies & Compliance' },
    { id: 'custom' as ProjectTemplate, icon: '📦', name: 'Custom', desc: 'Specialized Legal Structures' },
];

export const Projects: React.FC = () => {
    const navigate = useNavigate();
    const { apps, fetchApps, setCurrentApp } = useAppStore();
    const [isAdding, setIsAdding] = useState(false);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');

    const [newProject, setNewProject] = useState<NewProject>({
        name: '',
        appId: '',
        appUrl: '',
        description: '',
        template: 'pages',
    });

    useEffect(() => {
        fetchApps();
    }, [fetchApps]);

    const resetForm = () => {
        setNewProject({ name: '', appId: '', appUrl: '', description: '', template: 'pages' });
        setMessage('');
    };

    const handleCreate = async () => {
        if (!newProject.name.trim() || !newProject.appId.trim()) {
            setMessage('Please enter both Project Name and App ID.');
            return;
        }

        setSaving(true);
        setMessage('');

        try {
            const template = TEMPLATES.find(t => t.id === newProject.template);

            const response = await fetch(`${API_URL}/api/v1/apps`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: newProject.name,
                    description: newProject.description || template?.desc,
                    icon: template?.icon || '📄',
                    color: '#3B82F6',
                }),
            });

            const result = await response.json();

            if (result.success) {
                const createdAppId = result.data.id;

                // AUTO-INITIALIZE LEGAL PAGES if template is 'pages'
                if (newProject.template === 'pages') {
                    setMessage('✅ Project created! Initializing legal documents...');

                    // 1. Create Collection "Legal Pages"
                    const colResponse = await fetch(`${API_URL}/api/v1/collections`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            appId: createdAppId,
                            name: 'Legal Pages',
                            slug: 'legal_pages',
                            icon: '⚖️',
                            fields: [
                                { name: 'title', type: 'string', label: 'Title', required: true },
                                { name: 'slug', type: 'string', label: 'Slug', required: true },
                                { name: 'content', type: 'richtext', label: 'Content', required: false }
                            ]
                        }),
                    });
                    const colResult = await colResponse.json();

                    if (colResult.success) {
                        const colId = colResult.data.id;
                        const pages = [
                            { title: 'Privacy Policy', slug: 'privacy-policy' },
                            { title: 'Terms of Service', slug: 'terms-of-service' },
                            { title: 'About Us', slug: 'about-us' },
                            { title: 'Contact Us', slug: 'contact-us' }
                        ];

                        for (const page of pages) {
                            await fetch(`${API_URL}/api/v1/content/legal_pages`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                    appId: createdAppId,
                                    collectionId: colId,
                                    data: {
                                        title: page.title,
                                        slug: page.slug,
                                        content: `<h1>${page.title}</h1><p>Welcome to the ${page.title} for ${newProject.name}. This document was generated on ${new Date().toLocaleDateString()}.</p>`,
                                        app_id: newProject.appId,
                                        app_url: newProject.appUrl
                                    },
                                    status: 'published'
                                }),
                            });
                        }
                    }
                }

                setMessage('✅ Legal Project Ready!');
                fetchApps();
                setTimeout(() => {
                    setIsAdding(false);
                    resetForm();
                }, 1000);
            } else {
                setMessage(`❌ ${result.error?.message || 'Failed to create'}`);
            }
        } catch (error) {
            setMessage(`❌ ${String(error)}`);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id: string, name: string) => {
        if (!confirm(`Delete "${name}"?`)) return;
        try {
            await fetch(`${API_URL}/api/v1/apps/${id}`, { method: 'DELETE' });
            if (apps.find(a => a.id === id)?.id === (useAppStore.getState().currentApp as any)?.id) {
                setCurrentApp(null);
            }
            fetchApps();
        } catch (error) {
            console.error('Delete failed:', error);
        }
    };

    const handleSelectApp = (app: any) => {
        setCurrentApp(app);
        navigate('/');
    };

    return (
        <div className="animate-fade-in max-w-4xl mx-auto">
            {/* Header */}
            <header className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                        Projects
                    </h1>
                    <p style={{ color: 'var(--text-secondary)' }}>
                        Manage all your content projects
                    </p>
                </div>
                <button onClick={() => { setIsAdding(true); resetForm(); }} className="btn btn-primary">
                    + New Project
                </button>
            </header>

            {/* Projects Grid */}
            {apps.length === 0 ? (
                <div className="card p-12 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-indigo-500/10 flex items-center justify-center text-3xl mx-auto mb-6">
                        ⚖️
                    </div>
                    <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                        No projects yet
                    </h2>
                    <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>
                        Create your first project to start managing content.
                    </p>
                    <button onClick={() => { setIsAdding(true); resetForm(); }} className="btn btn-primary">
                        Create Project
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {apps.map((app) => (
                        <div
                            key={app.id}
                            className="card group overflow-hidden flex flex-col transition-all hover:scale-[1.02]"
                        >
                            <div className="p-6 flex-1 flex flex-col" onClick={() => handleSelectApp(app)}>
                                <div className="flex justify-between items-start mb-6">
                                    <div
                                        className="w-14 h-14 rounded-2xl flex items-center justify-center text-3xl shadow-lg"
                                        style={{ backgroundColor: `${app.color || '#3B82F6'}15`, border: '1px solid var(--border-color)' }}
                                    >
                                        {app.icon || '📂'}
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            className="opacity-0 group-hover:opacity-100 p-2.5 rounded-xl transition-all hover:bg-red-500/10 active:scale-95"
                                            style={{ color: 'var(--danger)' }}
                                            onClick={(e) => { e.stopPropagation(); handleDelete(app.id, app.name); }}
                                            title="Delete Project"
                                        >
                                            🗑️
                                        </button>
                                    </div>
                                </div>
                                <h3 className="font-bold text-lg mb-2" style={{ color: 'var(--text-primary)', fontFamily: 'Outfit, sans-serif' }}>
                                    {app.name}
                                </h3>
                                <p className="text-sm line-clamp-2 leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                                    {app.description || 'Manage your content with this project.'}
                                </p>
                            </div>
                            <div
                                className="px-6 py-3 text-[10px] font-black uppercase tracking-widest flex items-center justify-between"
                                style={{ backgroundColor: 'var(--bg-tertiary)', borderTop: '1px solid var(--border-color)' }}
                            >
                                <span style={{ color: 'var(--text-muted)' }}>Workspace</span>
                                <span className="text-blue-500 opacity-80">CONNECTED</span>
                            </div>
                        </div>
                    ))}

                    {/* Add Project Card */}
                    <button
                        onClick={() => { setIsAdding(true); resetForm(); }}
                        className="card p-5 flex flex-col items-center justify-center min-h-[160px] transition-all"
                        style={{
                            border: '2px dashed var(--border-color)',
                            backgroundColor: 'transparent',
                            color: 'var(--text-muted)'
                        }}
                    >
                        <span className="text-3xl mb-2">+</span>
                        <span className="font-medium">Add Project</span>
                    </button>
                </div>
            )}

            {/* Modal */}
            {isAdding && (
                <div
                    className="fixed inset-0 flex items-center justify-center z-50 p-4"
                    style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
                    onClick={() => setIsAdding(false)}
                >
                    <div
                        className="card w-full max-w-lg"
                        style={{ backgroundColor: 'var(--bg-primary)' }}
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div className="p-6" style={{ borderBottom: '1px solid var(--border-color)' }}>
                            <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
                                Create Project
                            </h2>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 space-y-5">
                            {message && (
                                <div className={`p-3 rounded-lg text-sm ${message.includes('✅') ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                                    {message}
                                </div>
                            )}

                            <div>
                                <label className="label">Project Name</label>
                                <input
                                    type="text"
                                    className="input"
                                    placeholder="e.g., My New App"
                                    value={newProject.name}
                                    onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="label text-xs uppercase opacity-70">Internal App ID</label>
                                    <input
                                        type="text"
                                        className="input font-mono text-sm"
                                        placeholder="com.example.app"
                                        value={newProject.appId}
                                        onChange={(e) => setNewProject({ ...newProject, appId: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="label text-xs uppercase opacity-70">App / Play Store URL</label>
                                    <input
                                        type="text"
                                        className="input text-sm"
                                        placeholder="https://..."
                                        value={newProject.appUrl}
                                        onChange={(e) => setNewProject({ ...newProject, appUrl: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="label">Content Type</label>
                                <div className="grid grid-cols-3 gap-2">
                                    {TEMPLATES.map((t) => (
                                        <button
                                            key={t.id}
                                            type="button"
                                            onClick={() => setNewProject({ ...newProject, template: t.id })}
                                            className="p-3 rounded-lg text-center transition-all"
                                            style={{
                                                backgroundColor: newProject.template === t.id ? 'var(--accent)' : 'var(--bg-secondary)',
                                                color: newProject.template === t.id ? 'white' : 'var(--text-primary)',
                                                border: `1px solid ${newProject.template === t.id ? 'var(--accent)' : 'var(--border-color)'}`
                                            }}
                                        >
                                            <div className="text-xl mb-1">{t.icon}</div>
                                            <div className="text-xs font-medium">{t.name}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="label">Description (optional)</label>
                                <input
                                    type="text"
                                    className="input"
                                    placeholder="What's this project for?"
                                    value={newProject.description}
                                    onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                                />
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="p-6 flex justify-end gap-3" style={{ borderTop: '1px solid var(--border-color)' }}>
                            <button
                                onClick={() => { setIsAdding(false); resetForm(); }}
                                className="btn btn-secondary"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreate}
                                className="btn btn-primary"
                                disabled={saving}
                            >
                                {saving ? 'Creating...' : 'Create Project'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
